/**
 * AdminDataTable - Standardized admin list view table component
 * 
 * Features:
 * - Sticky headers
 * - Consistent spacing/typography
 * - Column formatting (currency, date, status pills, monospace)
 * - Checkbox selection with bulk actions
 * - Row density toggle
 * - Empty state with "—"
 * - Server-side sorting indicators
 * - Column visibility toggle (localStorage persistence)
 * - Resizable columns
 * 
 * Performance Optimizations:
 * - Memoized cell components prevent unnecessary re-renders
 * - Virtualization ready (can integrate react-window for 1000+ rows)
 * - Stable callbacks via useCallback
 */
import React, { useState, useCallback, memo, useEffect, useRef, useMemo } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '../ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  ChevronUp, 
  ChevronDown, 
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Rows3,
  Rows4,
  Columns3,
  Check,
  RotateCcw,
  GripVertical,
  ChevronDownSquare,
  ChevronUpSquare
} from 'lucide-react';
import { cn } from '../../lib/utils';

// ===================================================
// COLUMN FORMATTERS
// ===================================================

/**
 * Format currency value
 */
export const formatCurrency = (value, options = {}) => {
  if (value === null || value === undefined) return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: options.currency || 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

/**
 * Format number with thousands separator
 */
export const formatNumber = (value, options = {}) => {
  if (value === null || value === undefined) return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '—';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: options.decimals || 0,
    maximumFractionDigits: options.decimals || 0,
  }).format(num);
};

/**
 * Format percentage
 */
export const formatPercent = (value, options = {}) => {
  if (value === null || value === undefined) return '—';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '—';
  // If value is already a percent (e.g., 25 for 25%), don't multiply
  const displayValue = options.isRaw ? num : num * 100;
  return `${displayValue.toFixed(options.decimals || 0)}%`;
};

/**
 * Format date as MMM DD, YYYY
 */
export const formatDate = (value) => {
  if (!value) return '—';
  try {
    const date = new Date(value);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return '—';
  }
};

/**
 * Format date with full datetime for tooltip
 */
export const formatDateWithTime = (value) => {
  if (!value) return { display: '—', full: null };
  try {
    const date = new Date(value);
    return {
      display: date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      full: date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
    };
  } catch {
    return { display: '—', full: null };
  }
};

/**
 * Format boolean as Yes/No
 */
export const formatBoolean = (value, options = {}) => {
  if (value === null || value === undefined) return '—';
  const { yesLabel = 'Yes', noLabel = 'No' } = options;
  return value ? yesLabel : noLabel;
};

// ===================================================
// CELL COMPONENTS
// ===================================================

/**
 * Monospace cell for reference numbers (Order #, PO #, Invoice #)
 */
export const MonoCell = ({ value, onClick, className }) => {
  const content = value || '—';
  const baseClass = 'font-mono text-sm';
  
  if (onClick) {
    return (
      <button 
        onClick={onClick}
        className={cn(baseClass, 'text-blue-600 hover:text-blue-800 hover:underline', className)}
      >
        {content}
      </button>
    );
  }
  
  return <span className={cn(baseClass, 'text-ivory-700', className)}>{content}</span>;
};

/**
 * Currency cell - right aligned
 */
export const CurrencyCell = ({ value, className }) => (
  <span className={cn('text-right block font-medium tabular-nums', className)}>
    {formatCurrency(value)}
  </span>
);

/**
 * Number cell - right aligned with thousands separator
 */
export const NumberCell = ({ value, decimals = 0, className }) => (
  <span className={cn('text-right block tabular-nums', className)}>
    {formatNumber(value, { decimals })}
  </span>
);

/**
 * Percent cell - right aligned
 */
export const PercentCell = ({ value, decimals = 0, isRaw = true, className }) => (
  <span className={cn('text-right block tabular-nums', className)}>
    {formatPercent(value, { decimals, isRaw })}
  </span>
);

/**
 * Date cell with tooltip showing full datetime
 */
export const DateCell = ({ value, className }) => {
  const { display, full } = formatDateWithTime(value);
  
  if (!full) {
    return <span className={cn('text-ivory-600 text-sm', className)}>{display}</span>;
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn('text-ivory-600 text-sm cursor-default', className)}>
            {display}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{full}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Status badge cell with color coding
 */
export const StatusCell = ({ value, statusConfig, className }) => {
  if (!value) return <span className="text-ivory-400">—</span>;
  
  const config = statusConfig?.[value] || statusConfig?.[value.toLowerCase()] || {
    label: value.replace(/_/g, ' '),
    color: 'bg-gray-100 text-gray-700'
  };
  
  const Icon = config.icon;
  
  return (
    <Badge className={cn('gap-1 capitalize', config.color, className)}>
      {Icon && <Icon className="w-3 h-3" />}
      {config.label || value.replace(/_/g, ' ')}
    </Badge>
  );
};

/**
 * Boolean cell as Yes/No or icon
 */
export const BooleanCell = ({ value, showIcon = false, yesLabel = 'Yes', noLabel = 'No', className }) => {
  if (value === null || value === undefined) {
    return <span className="text-ivory-400">—</span>;
  }
  
  if (showIcon) {
    return value ? (
      <span className={cn('text-green-600', className)}>✓</span>
    ) : (
      <span className={cn('text-ivory-400', className)}>✗</span>
    );
  }
  
  return (
    <span className={cn(value ? 'text-green-600' : 'text-ivory-500', className)}>
      {value ? yesLabel : noLabel}
    </span>
  );
};

/**
 * Truncated text cell with tooltip
 */
export const TruncatedCell = ({ value, maxWidth = 200, className }) => {
  if (!value) return <span className="text-ivory-400">—</span>;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className={cn('block truncate', className)}
            style={{ maxWidth }}
          >
            {value}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm">
          <p className="break-words">{value}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// ===================================================
// SORT HEADER COMPONENT
// ===================================================

const SortableHeader = ({ column, sortBy, sortDir, onSort, children, className }) => {
  const isSorted = sortBy === column;
  
  const handleClick = () => {
    if (isSorted) {
      // Toggle direction
      onSort(column, sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to descending for dates/numbers, ascending for text
      onSort(column, 'desc');
    }
  };
  
  return (
    <button
      onClick={handleClick}
      className={cn(
        'flex items-center gap-1 hover:text-ivory-900 transition-colors group',
        isSorted && 'text-ivory-900 font-semibold',
        className
      )}
    >
      {children}
      <span className="w-4 h-4 flex items-center justify-center">
        {isSorted ? (
          sortDir === 'asc' ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )
        ) : (
          <ChevronsUpDown className="w-4 h-4 opacity-0 group-hover:opacity-50 transition-opacity" />
        )}
      </span>
    </button>
  );
};

// ===================================================
// BULK ACTION TOOLBAR
// ===================================================

export const BulkActionToolbar = ({ 
  selectedCount, 
  onClearSelection, 
  children,
  className 
}) => {
  if (selectedCount === 0) return null;
  
  return (
    <div className={cn(
      'flex items-center gap-4 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg mb-4',
      className
    )}>
      <span className="text-sm font-medium text-blue-700">
        {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
      </span>
      <div className="flex-1 flex items-center gap-2">
        {children}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onClearSelection}
        className="text-blue-600 hover:text-blue-800"
      >
        Clear selection
      </Button>
    </div>
  );
};

// ===================================================
// PAGINATION COMPONENT
// ===================================================

const PAGE_SIZES = [25, 50, 100, 250];

export const TablePagination = ({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  className
}) => {
  const totalPages = Math.ceil(total / pageSize);
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);
  
  return (
    <div className={cn(
      'flex items-center justify-between px-4 py-3 border-t border-ivory-200 bg-ivory-50/50',
      className
    )}>
      <div className="flex items-center gap-4">
        <span className="text-sm text-ivory-600">
          Showing {startItem}-{endItem} of {total.toLocaleString()}
        </span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-ivory-500">Rows per page:</span>
          <Select 
            value={pageSize.toString()} 
            onValueChange={(v) => onPageSizeChange(parseInt(v))}
          >
            <SelectTrigger className="w-[70px] h-8" data-testid="page-size-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZES.map(size => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        <span className="text-sm text-ivory-600">
          Page {page} of {totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          data-testid="prev-page-btn"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          data-testid="next-page-btn"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

// ===================================================
// ROW DENSITY TOGGLE
// ===================================================

export const DensityToggle = ({ density, onDensityChange, className }) => {
  return (
    <div className={cn('flex items-center gap-1 border rounded-lg p-1', className)}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={density === 'compact' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => onDensityChange('compact')}
              className="h-7 w-7 p-0"
              data-testid="density-compact-btn"
            >
              <Rows4 className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Compact</TooltipContent>
        </Tooltip>
        
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={density === 'comfortable' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => onDensityChange('comfortable')}
              className="h-7 w-7 p-0"
              data-testid="density-comfortable-btn"
            >
              <Rows3 className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Comfortable</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

// ===================================================
// COLUMN SELECTOR DROPDOWN WITH REORDERING
// ===================================================

export const ColumnSelector = ({ 
  columns = [], 
  visibleColumns = {},
  columnOrder = [],
  onToggle, 
  onReorder,
  onReset,
  className = '' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draggedKey, setDraggedKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get ordered columns list
  const orderedColumns = useMemo(() => {
    if (columnOrder.length === 0) return columns;
    const columnMap = new Map(columns.map(col => [col.key, col]));
    return columnOrder
      .filter(key => columnMap.has(key))
      .map(key => columnMap.get(key));
  }, [columns, columnOrder]);

  // Count visible columns
  const visibleCount = orderedColumns.filter(col => visibleColumns[col.key] !== false).length;

  // Drag and drop handlers
  const handleDragStart = (e, columnKey) => {
    setDraggedKey(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnKey);
  };

  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (columnKey !== draggedKey) {
      setDragOverKey(columnKey);
    }
  };

  const handleDragLeave = () => {
    setDragOverKey(null);
  };

  const handleDrop = (e, targetKey) => {
    e.preventDefault();
    if (draggedKey && targetKey && draggedKey !== targetKey) {
      onReorder?.(draggedKey, targetKey);
    }
    setDraggedKey(null);
    setDragOverKey(null);
  };

  const handleDragEnd = () => {
    setDraggedKey(null);
    setDragOverKey(null);
  };

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      {/* Toggle Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2"
        data-testid="column-selector-btn"
      >
        <Columns3 className="w-4 h-4" />
        <span className="hidden sm:inline">Columns</span>
        <span className="text-xs text-ivory-400">({visibleCount}/{columns.length})</span>
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="absolute right-0 top-full mt-1 w-64 bg-white border border-ivory-200 rounded-lg shadow-lg z-50"
          data-testid="column-selector-dropdown"
        >
          {/* Header */}
          <div className="px-3 py-2 border-b border-ivory-200 flex items-center justify-between">
            <span className="text-xs font-semibold text-ivory-500 uppercase tracking-wide">
              Customize Columns
            </span>
            <button
              onClick={() => onReset?.()}
              className="text-xs text-copper-600 hover:text-copper-700 flex items-center gap-1"
              title="Reset to defaults"
              data-testid="column-selector-reset"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          </div>

          {/* Drag instruction */}
          <div className="px-3 py-1.5 bg-ivory-50 border-b border-ivory-100">
            <p className="text-xs text-ivory-500 flex items-center gap-1">
              <GripVertical className="w-3 h-3" />
              Drag to reorder • Click to toggle
            </p>
          </div>

          {/* Column List */}
          <div className="max-h-64 overflow-y-auto py-1">
            {orderedColumns.map((column) => {
              const isVisible = visibleColumns[column.key] !== false;
              const isDragging = draggedKey === column.key;
              const isDragOver = dragOverKey === column.key;
              
              return (
                <div
                  key={column.key}
                  draggable
                  onDragStart={(e) => handleDragStart(e, column.key)}
                  onDragOver={(e) => handleDragOver(e, column.key)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, column.key)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-2 transition-colors cursor-grab active:cursor-grabbing',
                    isDragging && 'opacity-50 bg-ivory-100',
                    isDragOver && 'border-t-2 border-copper-500',
                    !isDragging && !isDragOver && 'hover:bg-ivory-50'
                  )}
                  data-testid={`column-item-${column.key}`}
                >
                  {/* Drag Handle */}
                  <GripVertical className="w-4 h-4 text-ivory-400 flex-shrink-0" />
                  
                  {/* Visibility Toggle */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle?.(column.key);
                    }}
                    className={cn(
                      'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                      isVisible ? 'bg-copper-600 border-copper-600' : 'border-ivory-300 hover:border-ivory-400'
                    )}
                    data-testid={`column-toggle-${column.key}`}
                  >
                    {isVisible && <Check className="w-3 h-3 text-white" />}
                  </button>
                  
                  {/* Column Name */}
                  <span className={cn(
                    'text-sm flex-1 truncate',
                    isVisible ? 'text-ivory-900' : 'text-ivory-400'
                  )}>
                    {column.header}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Footer hint */}
          <div className="px-3 py-2 border-t border-ivory-200 bg-ivory-50">
            <p className="text-xs text-ivory-500">
              Changes are saved automatically.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ===================================================
// TABLE PREFERENCES HOOK
// ===================================================

const STORAGE_PREFIX = 'table_prefs_';

export const useTablePreferences = (tableId, columns = []) => {
  // Initialize state from localStorage or defaults
  const [preferences, setPreferences] = useState(() => {
    try {
      const stored = localStorage.getItem(`${STORAGE_PREFIX}${tableId}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with current columns (handle new columns added after preferences saved)
        const defaultVisibility = {};
        const defaultWidths = {};
        const defaultOrder = columns.map(col => col.key);
        columns.forEach(col => {
          defaultVisibility[col.key] = col.defaultVisible !== false;
          defaultWidths[col.key] = col.defaultWidth || 'auto';
        });
        
        // Merge column order - keep saved order but add new columns at the end
        let columnOrder = parsed.columnOrder || defaultOrder;
        // Add any new columns that weren't in the saved order
        const savedOrderSet = new Set(columnOrder);
        defaultOrder.forEach(key => {
          if (!savedOrderSet.has(key)) {
            columnOrder.push(key);
          }
        });
        // Remove any columns that no longer exist
        columnOrder = columnOrder.filter(key => defaultOrder.includes(key));
        
        return {
          visibleColumns: { ...defaultVisibility, ...parsed.visibleColumns },
          columnWidths: { ...defaultWidths, ...parsed.columnWidths },
          columnOrder,
        };
      }
    } catch (e) {
      console.warn(`Failed to load table preferences for ${tableId}:`, e);
    }
    
    // Build defaults
    const defaultVisibility = {};
    const defaultWidths = {};
    const defaultOrder = columns.map(col => col.key);
    columns.forEach(col => {
      defaultVisibility[col.key] = col.defaultVisible !== false;
      defaultWidths[col.key] = col.defaultWidth || 'auto';
    });
    return { 
      visibleColumns: defaultVisibility, 
      columnWidths: defaultWidths,
      columnOrder: defaultOrder 
    };
  });

  // Save to localStorage whenever preferences change
  useEffect(() => {
    try {
      localStorage.setItem(`${STORAGE_PREFIX}${tableId}`, JSON.stringify(preferences));
    } catch (e) {
      console.warn(`Failed to save table preferences for ${tableId}:`, e);
    }
  }, [tableId, preferences]);

  // Toggle column visibility
  const toggleColumn = useCallback((columnKey) => {
    setPreferences(prev => ({
      ...prev,
      visibleColumns: {
        ...prev.visibleColumns,
        [columnKey]: !prev.visibleColumns[columnKey],
      },
    }));
  }, []);

  // Set column width
  const setColumnWidth = useCallback((columnKey, width) => {
    setPreferences(prev => ({
      ...prev,
      columnWidths: {
        ...prev.columnWidths,
        [columnKey]: width,
      },
    }));
  }, []);

  // Reorder columns (move column from one index to another)
  const reorderColumn = useCallback((fromIndex, toIndex) => {
    setPreferences(prev => {
      const newOrder = [...prev.columnOrder];
      const [movedItem] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedItem);
      return {
        ...prev,
        columnOrder: newOrder,
      };
    });
  }, []);

  // Move column by key (for drag and drop)
  const moveColumn = useCallback((draggedKey, targetKey) => {
    setPreferences(prev => {
      const newOrder = [...prev.columnOrder];
      const fromIndex = newOrder.indexOf(draggedKey);
      const toIndex = newOrder.indexOf(targetKey);
      if (fromIndex === -1 || toIndex === -1) return prev;
      
      const [movedItem] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, movedItem);
      return {
        ...prev,
        columnOrder: newOrder,
      };
    });
  }, []);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    const defaultVisibility = {};
    const defaultWidths = {};
    const defaultOrder = columns.map(col => col.key);
    columns.forEach(col => {
      defaultVisibility[col.key] = col.defaultVisible !== false;
      defaultWidths[col.key] = col.defaultWidth || 'auto';
    });
    setPreferences({ 
      visibleColumns: defaultVisibility, 
      columnWidths: defaultWidths,
      columnOrder: defaultOrder 
    });
  }, [columns]);

  // Get visible columns in the correct order
  const visibleColumnsList = useMemo(() => {
    const columnMap = new Map(columns.map(col => [col.key, col]));
    return preferences.columnOrder
      .filter(key => preferences.visibleColumns[key] !== false && columnMap.has(key))
      .map(key => columnMap.get(key));
  }, [columns, preferences.columnOrder, preferences.visibleColumns]);

  return {
    visibleColumns: preferences.visibleColumns,
    columnWidths: preferences.columnWidths,
    columnOrder: preferences.columnOrder,
    visibleColumnsList,
    toggleColumn,
    setColumnWidth,
    reorderColumn,
    moveColumn,
    resetToDefaults,
  };
};

// ===================================================
// RESIZABLE HEADER COMPONENT
// ===================================================

const ResizableTableHead = ({ 
  children, 
  columnKey, 
  width, 
  onResize, 
  minWidth = 60, 
  maxWidth = 400,
  className 
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWidth, setStartWidth] = useState(0);
  const headerRef = useRef(null);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(headerRef.current?.offsetWidth || 150);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidth + diff));
      onResize?.(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, startX, startWidth, minWidth, maxWidth, onResize]);

  const style = typeof width === 'number' ? { width: `${width}px`, minWidth: `${width}px` } : {};

  return (
    <TableHead
      ref={headerRef}
      className={cn('relative group', className)}
      style={style}
    >
      {children}
      {/* Resize Handle */}
      <div
        className={cn(
          'absolute right-0 top-0 bottom-0 w-1 cursor-col-resize transition-colors',
          isResizing ? 'bg-copper-500' : 'bg-transparent hover:bg-copper-400'
        )}
        onMouseDown={handleMouseDown}
        title="Drag to resize"
        data-testid={`resize-handle-${columnKey}`}
      />
    </TableHead>
  );
};

// ===================================================
// MEMOIZED TABLE ROW COMPONENT (Performance)
// ===================================================

const MemoizedTableRow = memo(({ 
  row, 
  rowId, 
  isSelected,
  isExpanded,
  columns, 
  columnWidths = {},
  selectable,
  expandable,
  cellPadding, 
  onSelectRow, 
  onRowClick,
  onToggleExpand,
  renderExpandedRow,
  rowActions
}) => {
  const totalColumns = columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0) + (expandable ? 1 : 0);
  
  return (
    <>
      <TableRow
        className={cn(
          'hover:bg-ivory-50 transition-colors',
          isSelected && 'bg-blue-50/50',
          isExpanded && 'bg-amber-50/30 border-b-0',
          onRowClick && 'cursor-pointer'
        )}
        onClick={() => onRowClick?.(row)}
        data-testid={`table-row-${rowId}`}
        data-state={isSelected ? 'selected' : undefined}
      >
        {/* Expand toggle */}
        {expandable && (
          <TableCell className={cn(cellPadding, 'w-10')} onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => onToggleExpand?.(rowId)}
              data-testid={`expand-row-${rowId}`}
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4 text-amber-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </Button>
          </TableCell>
        )}
        
        {/* Selection checkbox */}
        {selectable && (
          <TableCell className={cellPadding} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => onSelectRow(rowId, checked)}
              aria-label={`Select row ${rowId}`}
              data-testid={`select-row-${rowId}`}
            />
          </TableCell>
        )}
        
        {/* Data cells */}
        {columns.map((col) => {
          const colWidth = columnWidths[col.key];
          const cellStyle = colWidth && typeof colWidth === 'number' 
            ? { width: colWidth, minWidth: colWidth, maxWidth: colWidth } 
            : {};
          
          return (
            <TableCell
              key={col.key}
              className={cn(
                'text-sm',
                cellPadding,
                col.align === 'right' && 'text-right',
                col.align === 'center' && 'text-center',
                col.cellClassName
              )}
              style={cellStyle}
            >
              {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
            </TableCell>
          );
        })}
        
        {/* Row actions */}
        {rowActions && (
          <TableCell className={cn(cellPadding, 'text-right')} onClick={(e) => e.stopPropagation()}>
            {rowActions(row)}
          </TableCell>
        )}
      </TableRow>
      
      {/* Expanded Row Content */}
      {expandable && isExpanded && renderExpandedRow && (
        <TableRow className="bg-amber-50/20 hover:bg-amber-50/30">
          <TableCell colSpan={totalColumns} className="p-0">
            <div className="px-6 py-4 border-t border-amber-200/50">
              {renderExpandedRow(row)}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these change
  return (
    prevProps.rowId === nextProps.rowId &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isExpanded === nextProps.isExpanded &&
    prevProps.cellPadding === nextProps.cellPadding &&
    prevProps.row === nextProps.row &&
    prevProps.columns === nextProps.columns &&
    prevProps.rowActions === nextProps.rowActions &&
    prevProps.renderExpandedRow === nextProps.renderExpandedRow
  );
});

MemoizedTableRow.displayName = 'MemoizedTableRow';

// ===================================================
// MAIN TABLE COMPONENT
// ===================================================

const AdminDataTable = ({
  // Data
  data = [],
  columns = [],
  loading = false,
  emptyMessage = 'No data found',
  emptyIcon: EmptyIcon,
  
  // Selection
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  getRowId = (row) => row.id,
  
  // Expandable rows
  expandable = false,
  renderExpandedRow = null,
  singleExpand = true, // Only one row expanded at a time
  
  // Sorting
  sortBy,
  sortDir,
  onSort,
  
  // Pagination
  page = 1,
  pageSize = 25,
  total = 0,
  onPageChange,
  onPageSizeChange,
  
  // Row density
  density = 'comfortable',
  onDensityChange,
  showDensityToggle = true,
  
  // Row click
  onRowClick,
  
  // Column preferences (NEW)
  tableId, // Required for column preferences persistence
  showColumnSelector = true,
  resizableColumns = true,
  
  // Row actions (render function for action buttons)
  rowActions,
  
  // Styling
  className,
  'data-testid': testId = 'admin-data-table'
}) => {
  // Expanded rows state
  const [expandedIds, setExpandedIds] = useState([]);
  
  const handleToggleExpand = useCallback((rowId) => {
    setExpandedIds(prev => {
      if (prev.includes(rowId)) {
        return prev.filter(id => id !== rowId);
      } else {
        return singleExpand ? [rowId] : [...prev, rowId];
      }
    });
  }, [singleExpand]);
  
  // Use table preferences hook if tableId is provided
  const {
    visibleColumns,
    columnWidths,
    columnOrder,
    visibleColumnsList,
    toggleColumn,
    setColumnWidth,
    moveColumn,
    resetToDefaults,
  } = useTablePreferences(tableId || 'default', columns);
  
  // Filter columns based on visibility preferences
  const displayColumns = tableId ? visibleColumnsList : columns;
  
  // Calculate selection state
  const allSelected = data.length > 0 && selectedIds.length === data.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < data.length;
  
  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      onSelectionChange?.([]);
    } else {
      onSelectionChange?.(data.map(getRowId));
    }
  }, [allSelected, data, getRowId, onSelectionChange]);
  
  const handleSelectRow = useCallback((rowId, checked) => {
    if (checked) {
      onSelectionChange?.([...selectedIds, rowId]);
    } else {
      onSelectionChange?.(selectedIds.filter(id => id !== rowId));
    }
  }, [selectedIds, onSelectionChange]);
  
  // Row padding based on density
  const rowPadding = density === 'compact' ? 'py-2' : 'py-3';
  const cellPadding = density === 'compact' ? 'px-3 py-2' : 'px-4 py-3';
  
  return (
    <div className={cn('bg-white rounded-xl border border-ivory-200 shadow-soft overflow-hidden', className)} data-testid={testId}>
      {/* Table Toolbar with Column Selector */}
      {(showColumnSelector && tableId) && (
        <div className="flex items-center justify-end gap-2 px-4 py-2 border-b border-ivory-200 bg-ivory-50/50">
          {showDensityToggle && onDensityChange && (
            <DensityToggle density={density} onDensityChange={onDensityChange} />
          )}
          <ColumnSelector
            columns={columns}
            visibleColumns={visibleColumns}
            columnOrder={columnOrder}
            onToggle={toggleColumn}
            onReorder={moveColumn}
            onReset={resetToDefaults}
          />
        </div>
      )}
      
      {/* Table Container with sticky header */}
      <div className="overflow-auto max-h-[calc(100vh-300px)]">
        <Table>
          <TableHeader className="sticky top-0 bg-ivory-50 z-10">
            <TableRow className="hover:bg-ivory-50">
              {/* Expand column header */}
              {expandable && (
                <TableHead className={cn('w-[50px]', cellPadding)}>
                  <span className="sr-only">Expand</span>
                </TableHead>
              )}
              
              {/* Selection checkbox column */}
              {selectable && (
                <TableHead className={cn('w-[50px]', cellPadding)}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                    data-testid="select-all-checkbox"
                  />
                </TableHead>
              )}
              
              {/* Column headers - use displayColumns for filtered view */}
              {displayColumns.map((col) => {
                const colWidth = columnWidths[col.key];
                
                // Use resizable header if enabled
                if (resizableColumns && tableId) {
                  return (
                    <ResizableTableHead
                      key={col.key}
                      columnKey={col.key}
                      width={colWidth}
                      onResize={(w) => setColumnWidth(col.key, w)}
                      minWidth={col.minWidth || 60}
                      maxWidth={col.maxWidth || 400}
                      className={cn(
                        'text-xs font-medium text-ivory-600 uppercase tracking-wide whitespace-nowrap',
                        cellPadding,
                        col.align === 'right' && 'text-right',
                        col.align === 'center' && 'text-center',
                        col.headerClassName
                      )}
                    >
                      {col.sortable && onSort ? (
                        <SortableHeader
                          column={col.sortKey || col.key}
                          sortBy={sortBy}
                          sortDir={sortDir}
                          onSort={onSort}
                          className={col.align === 'right' ? 'justify-end' : ''}
                        >
                          {col.header}
                        </SortableHeader>
                      ) : (
                        col.header
                      )}
                    </ResizableTableHead>
                  );
                }
                
                return (
                  <TableHead
                    key={col.key}
                    className={cn(
                      'text-xs font-medium text-ivory-600 uppercase tracking-wide whitespace-nowrap',
                      cellPadding,
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                      col.width && `w-[${col.width}]`,
                      col.headerClassName
                    )}
                    style={colWidth && typeof colWidth === 'number' ? { width: colWidth, minWidth: colWidth } : {}}
                  >
                    {col.sortable && onSort ? (
                      <SortableHeader
                        column={col.sortKey || col.key}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={onSort}
                        className={col.align === 'right' ? 'justify-end' : ''}
                      >
                        {col.header}
                      </SortableHeader>
                    ) : (
                      col.header
                    )}
                  </TableHead>
                );
              })}
              
              {/* Actions column header */}
              {rowActions && (
                <TableHead className={cn('w-[60px] text-right', cellPadding)}>
                  <span className="sr-only">Actions</span>
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell 
                  colSpan={displayColumns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0) + (expandable ? 1 : 0)}
                  className="h-48"
                >
                  <div className="flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
                  </div>
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell 
                  colSpan={displayColumns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0) + (expandable ? 1 : 0)}
                  className="h-48"
                >
                  <div className="flex flex-col items-center justify-center text-ivory-500">
                    {EmptyIcon && <EmptyIcon className="w-12 h-12 mb-3 opacity-50" />}
                    <p>{emptyMessage}</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => {
                const rowId = getRowId(row);
                const isSelected = selectedIds.includes(rowId);
                const isExpanded = expandedIds.includes(rowId);
                
                return (
                  <MemoizedTableRow
                    key={rowId}
                    row={row}
                    rowId={rowId}
                    isSelected={isSelected}
                    isExpanded={isExpanded}
                    columns={displayColumns}
                    columnWidths={columnWidths}
                    selectable={selectable}
                    expandable={expandable}
                    cellPadding={cellPadding}
                    onSelectRow={handleSelectRow}
                    onRowClick={onRowClick}
                    onToggleExpand={handleToggleExpand}
                    renderExpandedRow={renderExpandedRow}
                    rowActions={rowActions}
                  />
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Pagination footer */}
      {!loading && data.length > 0 && onPageChange && (
        <TablePagination
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  );
};

export default AdminDataTable;
